import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkflowService } from '../../../../data-access/workflow.service';
import { CanComponentDeactivate } from '../unsaved-changes.guard';
import { CaseReviewDateOverridesComponent } from '../case-review-date-overrides/case-review-date-overrides.component';
import {
  STAGE_DISPLAY_NAMES,
  WorkflowAttachment,
  WorkflowPacketDetail,
  WorkflowTaskStatus,
  WorkflowTaskSummary
} from '../../../../domain/types/workflow';
import { extractDateFromUtcString, extractErrorMessage, formatYear, getCaseStatusClass, getDisplayTaskStatus, getDisplayTaskStatusClass, getStageName, getTaskStatusClass, getWorkflowDisplayName } from '../workflow-utils';
import { StageStep, StageStepStatus } from '../../stage-stepper/stage-stepper.component';

export interface AttachmentGroup {
  stageName: string;
  stageLabel: string;
  slots: AttachmentSlotGroup[];
}

export interface AttachmentSlotGroup {
  documentSlot: string;
  attachments: WorkflowAttachment[];
}

@Component({
  selector: 'app-packet-detail',
  templateUrl: './packet-detail.component.html',
  styleUrls: ['./packet-detail.component.scss']
})
export class PacketDetailComponent implements OnInit, CanComponentDeactivate {
  caseId: number;
  packet: WorkflowPacketDetail | null = null;
  loading = true;
  error: string | null = null;

  // The override editor owns the unsaved-changes state; the route guard delegates
  // to it so leaving the packet page with unsaved overrides prompts first.
  @ViewChild(CaseReviewDateOverridesComponent) overrideEditor?: CaseReviewDateOverridesComponent;

  canDeactivate(): boolean {
    return this.overrideEditor ? this.overrideEditor.confirmLeave() : true;
  }

  stages: string[] = [];
  attachmentGroups: AttachmentGroup[] = [];

  getStageName = getStageName;
  getWorkflowDisplayName = getWorkflowDisplayName;
  formatYear = formatYear;
  getTaskStatusClass = getTaskStatusClass;
  getDisplayTaskStatus = getDisplayTaskStatus;
  getDisplayTaskStatusClass = getDisplayTaskStatusClass;
  getCaseStatusClass = getCaseStatusClass;
  extractDateFromUtcString = extractDateFromUtcString;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private workflowSvc: WorkflowService
  ) {
    this.caseId = +this.route.snapshot.params['caseId'];
  }

  async ngOnInit() {
    try {
      this.packet = await this.workflowSvc.getPacket(this.caseId);
      this.stages = [...(this.packet.stages || []), 'Complete'];
      this.sortTasksByStageOrder();
      this.attachmentGroups = this.buildAttachmentGroups();
    } catch (err: any) {
      this.error = err?.status === 403 ? extractErrorMessage(err, 'Access denied') : 'Failed to load packet';
      console.error(err);
    } finally {
      this.loading = false;
    }
  }

  // Parallel-aware: each stage's dot reflects its OWN tasks, not a single linear
  // pointer — gated cases run reviewer stages concurrently, so several stages can
  // be "active" at once. Completed = it has tasks and all are done; active = it
  // has an in-progress task; pending = upcoming / not yet materialized.
  getStageStatus(stage: string): StageStepStatus {
    if (!this.packet) return StageStepStatus.Pending;

    if (stage === 'Complete') {
      return this.packet.status === 'Complete' ? StageStepStatus.Completed : StageStepStatus.Pending;
    }

    const tasks = this.packet.tasks.filter(
      t => t.stage === stage && t.status !== WorkflowTaskStatus.Cancelled);
    if (tasks.length === 0) return StageStepStatus.Pending;
    if (tasks.every(t => t.status === WorkflowTaskStatus.Completed)) return StageStepStatus.Completed;
    // An open task whose gate has closed is Past Due, not active —
    // mirrors the gate the task-detail page already enforces.
    const openTasks = tasks.filter(t => t.status !== WorkflowTaskStatus.Completed);
    if (openTasks.every(t => t.gateStatus === 'Upcoming')) return StageStepStatus.Pending;
    if (openTasks.every(t => t.pastDue || t.gateStatus === 'PastDue')) return StageStepStatus.PastDue;
    return StageStepStatus.Active;
  }

  get stageSteps(): StageStep[] {
    return this.stages.map(s => ({ label: getStageName(s), status: this.getStageStatus(s) }));
  }

  private sortTasksByStageOrder() {
    if (!this.packet) return;
    // Build stage order: LetterOfEvaluation is parallel to Supervisors, so place it right after
    const stageOrder = [...this.packet.stages];
    const supIdx = stageOrder.indexOf('Supervisors');
    if (supIdx >= 0 && !stageOrder.includes('LetterOfEvaluation')) {
      stageOrder.splice(supIdx + 1, 0, 'LetterOfEvaluation');
    }
    this.packet.tasks.sort((a, b) => {
      const ai = stageOrder.indexOf(a.stage);
      const bi = stageOrder.indexOf(b.stage);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }

  get positionDisplay(): string | null {
    if (!this.packet) return null;
    if (!['Promotion', 'Merit', 'FiveYearReview'].includes(this.packet.workflowType)) return null;
    const from = this.packet.currentPositionLabel;
    const to = this.packet.proposedPositionLabel;
    if (from && to && from !== to) return `${from} \u2192 ${to}`;
    if (to) return `\u2192 ${to}`;
    return null;
  }

  openTask(task: WorkflowTaskSummary) {
    this.router.navigate(['/review/task', task.id]);
  }

  viewAttachment(att: WorkflowAttachment) {
    this.workflowSvc.viewAttachment(att.id);
  }

  downloadAttachment(att: WorkflowAttachment) {
    this.workflowSvc.downloadAttachment(att.id, att.fileName);
  }

  private buildAttachmentGroups(): AttachmentGroup[] {
    if (!this.packet || this.packet.attachments.length === 0) return [];

    const stageOrder = this.packet.stages || [];
    const hasStages = this.packet.attachments.some(a => a.stageName != null);

    // Group attachments by stageName
    const byStage = new Map<string, WorkflowAttachment[]>();
    for (const att of this.packet.attachments) {
      const stage = !hasStages ? '' : (att.stageName || 'Unknown');
      if (!byStage.has(stage)) byStage.set(stage, []);
      byStage.get(stage)!.push(att);
    }

    // Sort stages by the workflow stage order
    const sortedStages = [...byStage.keys()].sort((a, b) => {
      const ai = stageOrder.indexOf(a);
      const bi = stageOrder.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return sortedStages.map(stage => {
      const atts = byStage.get(stage)!;

      // Group by documentSlot within each stage
      const bySlot = new Map<string, WorkflowAttachment[]>();
      for (const att of atts) {
        const slot = att.documentSlot || att.fileName;
        if (!bySlot.has(slot)) bySlot.set(slot, []);
        bySlot.get(slot)!.push(att);
      }

      return {
        stageName: stage,
        stageLabel: stage ? (STAGE_DISPLAY_NAMES[stage] || stage) : '',
        slots: [...bySlot.entries()].map(([slot, slotAtts]) => ({
          documentSlot: slot,
          attachments: slotAtts
        }))
      };
    });
  }
}
